import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Layers, Play, Check, AlertCircle, Loader2, ChevronDown, ChevronRight, Clock } from 'lucide-react';

const ACCENT = '#f97316';

interface BulkResultItem {
  index: number;
  operation: string;
  status: 'success' | 'error';
  id?: string;
  error?: string;
}

interface BulkResult {
  id: string;
  product: string;
  resource: string;
  total: number;
  succeeded: number;
  failed: number;
  results: BulkResultItem[];
  created_at: string;
}

const BULK_TARGETS: Record<string, string[]> = {
  bridge: ['connections', 'import_jobs', 'webhooks', 'api_keys'],
};

const EXAMPLE_PAYLOAD = JSON.stringify({
  operations: [
    { operation: 'create', data: { name: 'Example record', status: 'active' } },
    { operation: 'create', data: { name: 'Another record', status: 'active' } },
  ],
  atomic: false,
}, null, 2);

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

export default function BulkOperationsPage() {
  const [product, setProduct] = useState('bridge');
  const [resource, setResource] = useState('');
  const [payload, setPayload] = useState(EXAMPLE_PAYLOAD);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [jsonError, setJsonError] = useState('');

  const [history, setHistory] = useState<BulkResult[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [expandedJobData, setExpandedJobData] = useState<BulkResult | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      const data = await api.get<{ bulk_jobs: BulkResult[] }>('/bulk-jobs');
      setHistory(data.bulk_jobs);
    } catch { /* empty */ }
    setLoadingHistory(false);
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const validatePayload = (val: string) => {
    setPayload(val);
    try {
      const parsed = JSON.parse(val);
      if (!parsed.operations || !Array.isArray(parsed.operations)) {
        setJsonError('Payload must have an "operations" array');
      } else if (parsed.operations.length > 1000) {
        setJsonError('Maximum 1000 operations per request');
      } else {
        setJsonError('');
      }
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  const handleExecute = async () => {
    if (!resource || jsonError) return;
    setExecuting(true);
    setResult(null);
    try {
      const body = JSON.parse(payload);
      const data = await api.post<{ bulk_result: BulkResult }>(`/v1/bulk/${product}/${resource}`, body);
      setResult(data.bulk_result);
      await loadHistory();
    } catch (err: any) {
      setResult({
        id: '',
        product,
        resource,
        total: 0,
        succeeded: 0,
        failed: 1,
        results: [{ index: 0, operation: 'unknown', status: 'error', error: err.message || 'Request failed' }],
        created_at: new Date().toISOString(),
      });
    }
    setExecuting(false);
  };

  const toggleExpand = async (id: string) => {
    if (expandedJob === id) {
      setExpandedJob(null);
      setExpandedJobData(null);
      return;
    }
    setExpandedJob(id);
    try {
      const data = await api.get<{ bulk_result: BulkResult }>(`/bulk-jobs/${id}`);
      setExpandedJobData(data.bulk_result);
    } catch { /* empty */ }
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: '0 0 1.5rem', color: '#111827' }}>Bulk Operations</h2>

      {/* Execute Panel */}
      <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem', color: '#111827' }}>Execute Bulk Operation</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Product</label>
            <select
              value={product}
              onChange={e => { setProduct(e.target.value); setResource(''); }}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
            >
              {Object.keys(BULK_TARGETS).map(p => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>Resource</label>
            <select
              value={resource}
              onChange={e => setResource(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', boxSizing: 'border-box' }}
            >
              <option value="">Select resource...</option>
              {(BULK_TARGETS[product] || []).map(r => (
                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' }}>
            Request Body (JSON)
          </label>
          <textarea
            value={payload}
            onChange={e => validatePayload(e.target.value)}
            spellCheck={false}
            style={{
              width: '100%', minHeight: 200, padding: '0.75rem',
              border: `1px solid ${jsonError ? '#ef4444' : '#d1d5db'}`,
              borderRadius: '0.375rem', fontSize: '0.8125rem', fontFamily: 'monospace',
              backgroundColor: '#1e1e1e', color: '#d4d4d4', resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          {jsonError && (
            <p style={{ fontSize: '0.75rem', color: '#ef4444', margin: '0.25rem 0 0' }}>{jsonError}</p>
          )}
          <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.25rem 0 0' }}>
            Max 1000 operations. Set "atomic": true for all-or-nothing execution.
          </p>
        </div>

        <button
          onClick={handleExecute}
          disabled={executing || !resource || !!jsonError}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.5rem 1.25rem', backgroundColor: ACCENT, color: '#fff',
            border: 'none', borderRadius: '0.375rem', cursor: executing ? 'wait' : 'pointer',
            fontWeight: 600, fontSize: '0.875rem', opacity: executing || !resource || !!jsonError ? 0.5 : 1,
          }}
        >
          {executing ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={16} />}
          {executing ? 'Executing...' : 'Execute'}
        </button>
      </div>

      {/* Results Panel */}
      {result && (
        <div style={{
          backgroundColor: '#fff', border: `1px solid ${result.failed > 0 ? '#fca5a5' : '#a7f3d0'}`,
          borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            {result.failed === 0 ? (
              <Check size={20} color="#059669" />
            ) : (
              <AlertCircle size={20} color="#dc2626" />
            )}
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, color: '#111827' }}>
              Results: {result.succeeded}/{result.total} succeeded
            </h3>
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem' }}>
            <div style={{ padding: '0.75rem 1rem', backgroundColor: '#d1fae5', borderRadius: '0.375rem' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#065f46' }}>{result.succeeded}</div>
              <div style={{ fontSize: '0.75rem', color: '#065f46' }}>Succeeded</div>
            </div>
            <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fee2e2', borderRadius: '0.375rem' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#991b1b' }}>{result.failed}</div>
              <div style={{ fontSize: '0.75rem', color: '#991b1b' }}>Failed</div>
            </div>
          </div>

          {result.results.length > 0 && (
            <div style={{ maxHeight: 300, overflowY: 'auto', borderRadius: '0.375rem', border: '1px solid #e5e7eb' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>#</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Operation</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Status</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>ID</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {result.results.map(r => (
                    <tr key={r.index} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: r.status === 'error' ? '#fef2f2' : undefined }}>
                      <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>{r.index}</td>
                      <td style={{ padding: '0.5rem 0.75rem', color: '#374151' }}>{r.operation}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.125rem 0.375rem', borderRadius: '9999px', fontSize: '0.6875rem', fontWeight: 600,
                          backgroundColor: r.status === 'success' ? '#d1fae5' : '#fee2e2',
                          color: r.status === 'success' ? '#065f46' : '#991b1b',
                        }}>
                          {r.status === 'success' ? <Check size={10} /> : <AlertCircle size={10} />}
                          {r.status}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280' }}>{r.id || '--'}</td>
                      <td style={{ padding: '0.5rem 0.75rem', color: '#dc2626', fontSize: '0.75rem' }}>{r.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* History */}
      <h3 style={{ fontSize: '1.125rem', fontWeight: 600, margin: '0 0 1rem', color: '#111827' }}>Recent Bulk Jobs</h3>

      {loadingHistory ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Loader2 size={32} color="#9ca3af" style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      ) : history.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          <Layers size={48} color="#d1d5db" style={{ marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#374151', margin: '0 0 0.5rem' }}>No Bulk Jobs Yet</h3>
          <p style={{ color: '#6b7280', maxWidth: 400, margin: '0 auto' }}>
            Execute a bulk operation above to get started.
          </p>
        </div>
      ) : (
        <div style={{ backgroundColor: '#fff', borderRadius: '0.5rem', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {history.map(job => (
            <div key={job.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
              <div
                onClick={() => toggleExpand(job.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.75rem 1rem', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {expandedJob === job.id ? <ChevronDown size={16} color="#6b7280" /> : <ChevronRight size={16} color="#6b7280" />}
                  <span style={{ fontWeight: 500, color: '#111827', fontSize: '0.875rem' }}>
                    {job.product}.{job.resource}
                  </span>
                  <span style={{
                    padding: '0.125rem 0.5rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600,
                    backgroundColor: job.failed === 0 ? '#d1fae5' : '#fef3c7',
                    color: job.failed === 0 ? '#065f46' : '#92400e',
                  }}>
                    {job.succeeded}/{job.total} OK
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.8125rem' }}>
                  <Clock size={14} />
                  {formatDate(job.created_at)}
                </div>
              </div>

              {expandedJob === job.id && expandedJobData && (
                <div style={{ padding: '0 1rem 1rem 2.5rem' }}>
                  <div style={{ maxHeight: 250, overflowY: 'auto', borderRadius: '0.375rem', border: '1px solid #e5e7eb' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>#</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Op</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Status</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>ID</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expandedJobData.results.map(r => (
                          <tr key={r.index} style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: r.status === 'error' ? '#fef2f2' : undefined }}>
                            <td style={{ padding: '0.375rem 0.5rem', color: '#6b7280' }}>{r.index}</td>
                            <td style={{ padding: '0.375rem 0.5rem', color: '#374151' }}>{r.operation}</td>
                            <td style={{ padding: '0.375rem 0.5rem' }}>
                              <span style={{
                                padding: '0.125rem 0.375rem', borderRadius: '9999px', fontSize: '0.6875rem', fontWeight: 600,
                                backgroundColor: r.status === 'success' ? '#d1fae5' : '#fee2e2',
                                color: r.status === 'success' ? '#065f46' : '#991b1b',
                              }}>
                                {r.status}
                              </span>
                            </td>
                            <td style={{ padding: '0.375rem 0.5rem', fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280' }}>{r.id || '--'}</td>
                            <td style={{ padding: '0.375rem 0.5rem', color: '#dc2626', fontSize: '0.75rem' }}>{r.error || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
